import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/useMobile";
import { trpc } from "@/lib/trpc";
import { 
  Users, Plus, Pencil, Trash2, Mail, Phone, Loader2, User, Link2, Link2Off, UserPlus, MoreVertical, AlertTriangle
} from "lucide-react";
import { CSSProperties, useEffect, useState } from "react";
import { Link } from "wouter";
import { DashboardLayoutSkeleton } from "@/components/DashboardLayoutSkeleton";
import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { toast } from "sonner";
import { hasAnyRole, hasRole } from "@/lib/roles";
import { trackEvent, EVENTS } from "@/lib/tracking";

// Role name translations
const ROLE_LABELS: Record<string, string> = {
  user: "Benutzer",
  instructor: "Kursleiter",
  courseadmin: "Kursadmin",
  admin: "Admin",
};

const translateRoles = (roles: string | null | undefined): string => {
  if (!roles) return "";
  const roleList = roles.split(',').map(r => r.trim());
  // Hide 'user' role when other roles exist
  const displayRoles = roleList.filter(r => r !== 'user' || roleList.length === 1);
  return displayRoles.map(r => ROLE_LABELS[r] || r).join(', ');
};

type InstructorFormData = {
  name: string;
  email: string;
  phone: string;
  bio: string;
  isActive: boolean;
  userId: number | null;
  createUserAccount: boolean;
};

const defaultFormData: InstructorFormData = {
  name: "",
  email: "",
  phone: "",
  bio: "",
  isActive: true,
  userId: null,
  createUserAccount: false,
};

export default function AdminInstructors() {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) return <DashboardLayoutSkeleton />;
  if (!user || !hasAnyRole(user.roles, ["admin", "courseadmin"])) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <h1 className="text-2xl font-semibold">Zugriff verweigert</h1>
          <Link href="/"><Button>Zur Startseite</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}>
      <AdminInstructorsContent setSidebarWidth={setSidebarWidth} />
    </SidebarProvider>
  );
}

function AdminInstructorsContent({ setSidebarWidth }: { setSidebarWidth: (width: number) => void }) {
  const isMobile = useIsMobile();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState<number | null>(null);
  const [formData, setFormData] = useState<InstructorFormData>(defaultFormData);
  
  // Delete instructor state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteErrorDialogOpen, setDeleteErrorDialogOpen] = useState(false);
  const [instructorToDelete, setInstructorToDelete] = useState<{ id: number; name: string } | null>(null);
  const [checkingDelete, setCheckingDelete] = useState(false);

  const utils = trpc.useUtils();
  const { data: instructors, isLoading } = trpc.instructor.list.useQuery();
  const { data: users } = trpc.user.list.useQuery();

  // Show all users in link dropdown
  const linkableUsers = users || [];

  const createMutation = trpc.instructor.create.useMutation({
    onSuccess: (result) => {
      // Track instructor creation
      trackEvent(EVENTS.INSTRUCTOR_CREATED, { instructor_id: result.id, instructor_name: formData.name });
      toast.success("Kursleiter erstellt");
      utils.instructor.list.invalidate();
      utils.user.list.invalidate();
      setDialogOpen(false);
      setFormData(defaultFormData);
    },
    onError: (error) => toast.error(error.message),
  });

  const updateMutation = trpc.instructor.update.useMutation({
    onSuccess: () => {
      toast.success("Kursleiter aktualisiert");
      utils.instructor.list.invalidate();
      utils.user.list.invalidate();
      setDialogOpen(false);
      setEditingInstructor(null);
      setFormData(defaultFormData);
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteMutation = trpc.instructor.delete.useMutation({
    onSuccess: () => {
      toast.success("Kursleiter gelöscht");
      utils.instructor.list.invalidate();
      setDeleteDialogOpen(false);
      setInstructorToDelete(null);
    },
    onError: (error) => {
      setDeleteDialogOpen(false);
      if (error.message.includes("Kurse zugeordnet")) {
        setDeleteErrorDialogOpen(true);
      } else {
        toast.error(error.message);
        setInstructorToDelete(null);
      }
    },
  });

  // Function to check if instructor can be deleted before showing dialog
  const handleDeleteClick = async (instructor: { id: number; name: string }) => {
    setInstructorToDelete(instructor);
    setCheckingDelete(true);
    try {
      const result = await utils.instructor.checkCanDelete.fetch({ id: instructor.id });
      if (result.canDelete) {
        setDeleteDialogOpen(true);
      } else {
        setDeleteErrorDialogOpen(true);
      }
    } catch (error) {
      toast.error("Fehler beim Prüfen der Löschbarkeit");
      setInstructorToDelete(null);
    } finally {
      setCheckingDelete(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingInstructor) {
      // For updates, explicitly send null for cleared fields
      const data = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        bio: formData.bio || null,
        isActive: formData.isActive,
        userId: formData.userId ?? null,
        createUserAccount: formData.createUserAccount,
      };
      updateMutation.mutate({ id: editingInstructor, data });
    } else {
      // For creates, use undefined for optional fields
      const data = {
        name: formData.name,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        bio: formData.bio || undefined,
        isActive: formData.isActive,
        userId: formData.userId || undefined,
        createUserAccount: formData.createUserAccount,
      };
      createMutation.mutate(data);
    }
  };

  const openEditDialog = (instructor: NonNullable<typeof instructors>[0]) => {
    setEditingInstructor(instructor.id);
    setFormData({
      name: instructor.name,
      email: instructor.email || "",
      phone: instructor.phone || "",
      bio: instructor.bio || "",
      isActive: instructor.isActive,
      userId: instructor.userId,
      createUserAccount: false,
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingInstructor(null);
    setFormData(defaultFormData);
    setDialogOpen(true);
  };

  // Find linked user for an instructor
  const getLinkedUser = (userId: number | null) => {
    if (!userId) return null;
    return users?.find(u => u.id === userId);
  };

  return (
    <>
      <AdminSidebar setSidebarWidth={setSidebarWidth} />

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <span className="tracking-tight text-foreground font-medium">Kursleiter</span>
            </div>
          </div>
        )}
        <main className="flex-1 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Kursleiter</h1>
              <p className="text-muted-foreground">Verwalten Sie Ihre Kursleiter</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreateDialog} className="gap-2 bg-primary hover:bg-primary/90 w-full sm:w-auto">
                  <Plus className="h-4 w-4" />
                  <span>Neuer Kursleiter</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingInstructor ? "Kursleiter bearbeiten" : "Neuen Kursleiter erstellen"}</DialogTitle>
                  <DialogDescription>
                    {editingInstructor ? "Bearbeiten Sie die Kursleiter-Details" : "Füllen Sie das Formular aus"}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input id="name" required value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Vollständiger Name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail</Label>
                    <Input id="email" type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} placeholder="email@beispiel.de" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefon</Label>
                    <Input id="phone" type="tel" value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder="+49 ..." />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bio">Biografie</Label>
                    <Textarea id="bio" value={formData.bio} onChange={e => setFormData(p => ({ ...p, bio: e.target.value }))} placeholder="Kurze Beschreibung..." rows={3} />
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch id="isActive" checked={formData.isActive} onCheckedChange={v => setFormData(p => ({ ...p, isActive: v }))} />
                    <Label htmlFor="isActive">Aktiv</Label>
                  </div>

                  {/* User Linking Section */}
                  <div className="border-t pt-4 mt-4">
                    <Label className="text-sm font-medium mb-3 block">Benutzer-Verknüpfung</Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      Verknüpfen Sie diesen Kursleiter mit einem Benutzerkonto, damit er sich anmelden und seine Kurse verwalten kann.
                    </p>
                    
                    <div className="space-y-3">
                      <Select 
                        value={formData.userId?.toString() || "none"} 
                        onValueChange={(v) => setFormData(p => ({ 
                          ...p, 
                          userId: v === "none" ? null : parseInt(v),
                          createUserAccount: false 
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Benutzer auswählen..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            <span className="flex items-center gap-2">
                              <Link2Off className="h-4 w-4" />
                              Keine Verknüpfung
                            </span>
                          </SelectItem>
                          {linkableUsers.map(u => (
                            <SelectItem key={u.id} value={u.id.toString()}>
                              <span className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                {u.name || u.email} 
                                <Badge variant="outline" className="ml-1 text-xs">
                                  {translateRoles(u.roles)}
                                </Badge>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {!editingInstructor && !formData.userId && formData.email && (
                        <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                          <Switch 
                            id="createUserAccount" 
                            checked={formData.createUserAccount} 
                            onCheckedChange={v => setFormData(p => ({ ...p, createUserAccount: v }))} 
                          />
                          <div>
                            <Label htmlFor="createUserAccount" className="cursor-pointer">Benutzerkonto erstellen</Label>
                            <p className="text-xs text-muted-foreground">
                              Erstellt ein Benutzerkonto mit Kursleiter-Rolle und sendet eine Einladung per E-Mail
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
                    <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending} className="bg-primary hover:bg-primary/90">
                      {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {editingInstructor ? "Speichern" : "Erstellen"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-48" />)}
            </div>
          ) : instructors?.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Keine Kursleiter vorhanden</h3>
                <p className="text-muted-foreground mb-4">Fügen Sie Ihren ersten Kursleiter hinzu.</p>
                <Button onClick={openCreateDialog} className="bg-primary hover:bg-primary/90">
                  <Plus className="mr-2 h-4 w-4" />
                  Ersten Kursleiter erstellen
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {instructors?.map(instructor => {
                const linkedUser = getLinkedUser(instructor.userId);
                return (
                  <Card key={instructor.id} className={`relative ${!instructor.isActive ? "opacity-60" : ""}`}>
                    {/* Three-dot menu */}
                    <div className="absolute top-3 right-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(instructor)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Bearbeiten
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDeleteClick({ id: instructor.id, name: instructor.name })}
                            className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/50"
                            disabled={checkingDelete}
                          >
                            <Trash2 className="h-4 w-4 mr-2 text-red-600" />
                            {checkingDelete ? "Prüfe..." : "Löschen"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 shrink-0">
                          <User className="h-6 w-6 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0 pr-8">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold truncate">{instructor.name}</h3>
                            {!instructor.isActive && <Badge variant="outline">Inaktiv</Badge>}
                            {linkedUser ? (
                              <Badge variant="secondary" className="gap-1">
                                <Link2 className="h-3 w-3" />
                                Verknüpft
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="gap-1 text-muted-foreground">
                                <Link2Off className="h-3 w-3" />
                                Kein Login
                              </Badge>
                            )}
                          </div>
                          {instructor.email && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1 truncate">
                              <Mail className="h-3 w-3 shrink-0" />
                              {instructor.email}
                            </p>
                          )}
                          {instructor.phone && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Phone className="h-3 w-3 shrink-0" />
                              {instructor.phone}
                            </p>
                          )}
                        </div>
                      </div>
                      {instructor.bio && (
                        <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{instructor.bio}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </main>
      </SidebarInset>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kursleiter löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie "{instructorToDelete?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setInstructorToDelete(null)}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => instructorToDelete && deleteMutation.mutate({ id: instructorToDelete.id })}
              className="bg-destructive hover:bg-destructive/90"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Error Dialog */}
      <AlertDialog open={deleteErrorDialogOpen} onOpenChange={(open) => {
        setDeleteErrorDialogOpen(open);
        if (!open) setInstructorToDelete(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Kursleiter kann nicht gelöscht werden
            </AlertDialogTitle>
            <AlertDialogDescription>
              "{instructorToDelete?.name}" ist noch Kursen zugeordnet. Bitte weisen Sie diese Kurse zuerst einem anderen Kursleiter zu.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Verstanden</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
